import { ActionReducerMapBuilder, AsyncThunk } from "@reduxjs/toolkit";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "./store";
import { FetchableState } from "./fetchableState";

export interface EntityState<T> {
    entities: { [id: string]: FetchableState<T> };
}

export function addEntityCreateReducers<T>(
    builder: ActionReducerMapBuilder<any>,
    thunk: AsyncThunk<any, any, any>,
    entitySelector: (payload: any) => T,
    intercept?: (state: any, entityData: T) => void
) {
    builder.addCase(thunk.pending, (state, action) => {
        // we don't even know the ID yet; not a big deal, POST is not expected to be idempotent anyway
    })
    builder.addCase(thunk.fulfilled, (state, action) => {
        const entityData = entitySelector(action.payload);
        const id = (entityData as any).id;
        // if (state.entities[id])     // already there!
        //     return;
        state.entities[id] = {
            data: entityData,
            currentRequestId: undefined,
            lastFetched: Date.now(),
        }
        intercept?.(state, entityData)
    })
    builder.addCase(thunk.rejected, (state, action) => {
    })
}

export function addEntityFetchReducers<T, Payload, ThunkArg extends { id: string }>(
    builder: ActionReducerMapBuilder<any>,
    payloadSelector: (payload: Payload) => T,
    thunk: AsyncThunk<any, ThunkArg, any>,
) {
    builder.addCase(thunk.pending, (state, action) => {
        const id = action.meta.arg.id;
        let entity = state.entities[id];
        if (entity) {
            if (entity.currentRequestId)        // ongoing request
                return;
            entity.currentRequestId = action.meta.requestId;
        } else {
            state.entities[id] = {
                data: undefined,
                lastFetched: undefined,
                currentRequestId: action.meta.requestId,
                error: undefined,
            }
        }
    })
    builder.addCase(thunk.fulfilled, (state, action) => {
        const id = action.meta.arg.id;
        const entity = state.entities[id];
        if (entity && entity.currentRequestId !== action.meta.requestId)
            return;
        if (entity) {
            entity.currentRequestId = undefined;
            entity.lastFetched = Date.now();
            entity.data = payloadSelector(action.payload);
        }
    })
    builder.addCase(thunk.rejected, (state, action) => {
        const id = action.meta.arg.id;
        const entity = state.entities[id];
        if (entity && entity.currentRequestId !== action.meta.requestId)
            return;
        if (entity) {
            entity.currentRequestId = undefined;
            entity.error = action.error;
        }
    })
}

export function addEntityUpdateReducers<T, ThunkArg extends { id: string, updates: Partial<T> } = { id: string, updates: Partial<T> }>(
    builder: ActionReducerMapBuilder<any>,
    thunk: AsyncThunk<any, ThunkArg, any>,
    payloadSelector: (payload: any) => T
) {
    builder.addCase(thunk.pending, (state, action) => {
        const { id, updates } = action.meta.arg;
        let entity = state.entities[id];
        if (!entity || entity.currentRequestId)     // not there or ongoing request
            return;
        entity.currentRequestId = action.meta.requestId;
        entity.oldData = { ...entity.data };
        Object.entries(updates).forEach(([key, value]) => {
            (entity.data as any)[key] = value;
        })
    })
    builder.addCase(thunk.fulfilled, (state, action) => {
        const { id } = action.meta.arg;
        const entity = state.entities[id];
        if (entity.currentRequestId !== action.meta.requestId)
            return;
        entity.currentRequestId = undefined;
        entity.lastFetched = Date.now();
        entity.data = payloadSelector(action.payload);
        entity.oldData = undefined;
    })
    builder.addCase(thunk.rejected, (state, action) => {
        const { id } = action.meta.arg;
        const entity = state.entities[id];
        if (entity.currentRequestId !== action.meta.requestId)
            return;
        entity.currentRequestId = undefined;
        entity.data = { ...entity.oldData };
        entity.oldData = undefined;
        entity.error = action.error;
    })
}

export function addEntityDeleteReducers(
    builder: ActionReducerMapBuilder<any>,
    thunk: AsyncThunk<any, { id: string }, any>,
    intercept?: (state: any, id: string) => void
) {
    builder.addCase(thunk.pending, (state, action) => {
        const id = action.meta.arg.id;
        let entity = state.entities[id];
        if (!entity || entity.currentRequestId)
            return;
        entity.currentRequestId = action.meta.requestId;
    })
    builder.addCase(thunk.fulfilled, (state, action) => {
        const id = action.meta.arg.id;
        const entity = state.entities[id];
        if (entity && entity.currentRequestId !== action.meta.requestId)
            return;
        delete state.entities[id];
        intercept?.(state, id)
    })
    builder.addCase(thunk.rejected, (state, action) => {
        const id = action.meta.arg.id;
        const entity = state.entities[id];
        if (entity && entity.currentRequestId !== action.meta.requestId)
            return;
        if (entity) {
            entity.currentRequestId = undefined;
            entity.error = action.error;
        }
    })
}

export function useRealtimeEntity({ selector, action }: { selector: (state: any) => any, action: any }, pollInterval = 10 * 1000) {
    const dispatch = useDispatch<AppDispatch>();
    const entity = useSelector(selector);
    const [suspended, setSuspended] = useState(false);

    useEffect(() => {
        if (!suspended) {
            const interval = setInterval(() => {
                if (!suspended)
                    dispatch(action);
            }, pollInterval);
            return () => {
                clearInterval(interval);
            }
        }
    }, [dispatch, action, pollInterval, suspended]);

    return { entity, setSuspended };
}


